import { useCallback, useEffect, useRef, useState } from 'react';

import { EAsyncStateStatus, IUseAsyncStateResult, TAsyncState } from './types';

interface IStateUpdater<DataType, ErrorType> {
  (current: TAsyncState<DataType, ErrorType>): TAsyncState<DataType, ErrorType>;
}

export function useAsyncState<DataType, ErrorType = Error>(): IUseAsyncStateResult<DataType, ErrorType> {
  const [state, setState] = useState<TAsyncState<DataType, ErrorType>>(() => [
    undefined,
    {
      error: null,
      status: EAsyncStateStatus.Awaiting,
      isAwaiting: true,
      isPending: false,
      isRejected: false,
      isResolved: false,
    },
  ]);

  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  });

  const setStateIfMounted = useCallback<(updater: IStateUpdater<DataType, ErrorType>) => void>(updater => {
    /* istanbul ignore next */
    if (!isMounted.current) {
      return;
    }

    setState(updater);
  }, []);

  const markAsPending = useCallback<() => void>(() => {
    setStateIfMounted(state => {
      return [
        state[0],
        {
          error: null,
          status: EAsyncStateStatus.Pending,
          isAwaiting: false,
          isPending: true,
          isRejected: false,
          isResolved: false,
        },
      ];
    });
  }, [setStateIfMounted]);

  const markAsRejected = useCallback<(error: ErrorType) => void>(
    error => {
      setStateIfMounted(() => {
        return [
          undefined,
          {
            error,
            status: EAsyncStateStatus.Rejected,
            isAwaiting: false,
            isPending: false,
            isRejected: true,
            isResolved: false,
          },
        ];
      });
    },
    [setStateIfMounted],
  );

  const markAsResolved = useCallback<(data: DataType) => void>(
    data => {
      setStateIfMounted(() => {
        return [
          data,
          {
            error: null,
            status: EAsyncStateStatus.Resolved,
            isAwaiting: false,
            isPending: false,
            isRejected: false,
            isResolved: true,
          },
        ];
      });
    },
    [setStateIfMounted],
  );

  const reset = useCallback<() => void>(() => {
    setStateIfMounted(() => {
      return [
        undefined,
        {
          error: null,
          status: EAsyncStateStatus.Awaiting,
          isAwaiting: true,
          isPending: false,
          isRejected: false,
          isResolved: false,
        },
      ];
    });
  }, [setStateIfMounted]);

  return {
    state,
    markAsPending,
    markAsRejected,
    markAsResolved,
    reset,
  };
}
